import React, { useState, useRef, useEffect } from 'react';
import { Eye, ChevronDown, ShieldCheck, Lock } from 'lucide-react';

/**
 * "View Their Side" chooser for PM portal accounts. Offers:
 *   • View Read-Only  (existing read-only preview — unchanged behavior)
 *   • Open With Full Access  (JWT impersonation — Super Admin + Admin only)
 * Renders nothing if the viewer can neither preview nor full-access.
 */
export const PmViewSideChooser = ({
  testidBase,
  canPreview,
  canFullAccess,
  onReadOnly,
  onFullAccess,
  disabled,
  disabledTitle,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  if (!canPreview && !canFullAccess) return null;

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid={`${testidBase}-btn`}
        disabled={disabled}
        title={disabled ? (disabledTitle || '') : 'View this portal'}
        onClick={() => setOpen((v) => !v)}
        className={`text-xs border rounded px-2.5 h-8 inline-flex items-center gap-1.5 text-cyan-300 border-cyan-700/40 hover:bg-cyan-900/20 ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
      >
        <Eye className="w-3.5 h-3.5" /> View Their Side <ChevronDown className="w-3 h-3" />
      </button>
      {open && !disabled && (
        <div
          data-testid={`${testidBase}-menu`}
          className="absolute right-0 mt-1 w-56 bg-[#141414] border border-[#262626] rounded-md shadow-xl z-40 overflow-hidden"
        >
          {canPreview && (
            <button
              data-testid={`${testidBase}-readonly`}
              onClick={() => { setOpen(false); onReadOnly?.(); }}
              className="w-full text-left px-3 py-2.5 hover:bg-[#1f1f1f] flex items-start gap-2"
            >
              <Eye className="w-4 h-4 text-cyan-300 mt-0.5 shrink-0" />
              <span>
                <span className="block text-white text-sm">View Read-Only</span>
                <span className="block text-[#737373] text-[11px]">Look around, cannot change anything</span>
              </span>
            </button>
          )}
          {canFullAccess && (
            <button
              data-testid={`${testidBase}-fullaccess`}
              onClick={() => { setOpen(false); onFullAccess?.(); }}
              className="w-full text-left px-3 py-2.5 hover:bg-[#2a1a0f] flex items-start gap-2 border-t border-[#262626]"
            >
              <ShieldCheck className="w-4 h-4 text-orange-400 mt-0.5 shrink-0" />
              <span>
                <span className="block text-orange-200 text-sm">Open With Full Access</span>
                <span className="block text-[#737373] text-[11px]">Act as this user with their permissions</span>
              </span>
            </button>
          )}
          {!canFullAccess && canPreview && (
            <div className="px-3 py-2 text-[10px] text-[#525252] border-t border-[#262626] flex items-center gap-1.5">
              <Lock className="w-3 h-3" /> Full access is Admin-only
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PmViewSideChooser;
